import React, {
  ReactElement,
  Ref,
  forwardRef,
  useEffect,
  useState,
} from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import QrCodeDialog from "../../../../components/QrCodeDialog";
import LoadingButton from "../../../../components/ui/LoadingButton";
import api from "../../../../services/api";
import { toast } from "react-toastify";
import { useCountry } from "../../../../contexts/CountryContext";
import { useNavigate } from "react-router-dom";
import { useIntl } from "../../../../contexts/IntlContext";

const Transition = forwardRef(function Transition( 
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type Props = {
  establishment: {
    name: string;
    state: string;
    city: string;
  };

  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function CreateLinkDialog(props: Props) {
  const [linkCreated, setLinkCreated] = useState<string | null>(null);
  const [qrOpen, setQrOpen] = useState(false);
  const { country } = useCountry();
  const navigate = useNavigate();
  const intl = useIntl();

  useEffect(() => {
    setLinkCreated(null);
    setQrOpen(false);
  }, [props.open]);

  async function handleCreateLink(): Promise<void> {
    try {
      const response = await api.post("/app/establishment-hashes", {
        establishmentName: props.establishment.name,
        establishmentState: props.establishment.state,
        establishmentCity: props.establishment.city,
        establishmentCountry: country,
      });

      setLinkCreated(response.data.hashUrl);
      toast.success(intl.formatMessage({ id: 'createLinkDialog.success' }));
    } catch (error) {
      console.error(error);
      toast.error(intl.formatMessage({ id: 'createLinkDialog.error' }));
    }
  }

  return (
    <Dialog
      open={props.open}
      TransitionComponent={Transition}
      keepMounted
      onClose={props.setOpen.bind(null, false)}
      aria-describedby="alert-dialog-slide-description"
    >
      {!linkCreated ? (
        <>
          <DialogTitle>{intl.formatMessage({ id: 'createLinkDialog.title' })}</DialogTitle>
          <DialogContent sx={{ wordBreak: "break-word" }}>
            <DialogContentText id="alert-dialog-slide-description">
              <p>{intl.formatMessage({ id: 'common.name' })}: {props.establishment?.name}</p>
              <p>{intl.formatMessage({ id: 'common.state' })}: {props.establishment?.state}</p>
              <p>{intl.formatMessage({ id: 'common.city' })}: {props.establishment?.city}</p>
              <p>{intl.formatMessage({ id: 'common.country' })}: {country}</p>
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <LoadingButton onClick={handleCreateLink}>{intl.formatMessage({ id: 'createLinkDialog.createButton' })}</LoadingButton>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle sx={{ color: "success.main" }}>
            {intl.formatMessage({ id: 'createLinkDialog.successTitle' })}
          </DialogTitle>

          <DialogContent sx={{ wordBreak: "break-word" }}>
            <DialogContentText>{intl.formatMessage({ id: 'createLinkDialog.linkLabel' })}:</DialogContentText>
            <DialogContentText sx={{ color: "success.main" }}>
              {linkCreated}
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <LoadingButton fullWidth={false} variant="text" onClick={() => setQrOpen(true)}>
              {intl.formatMessage({ id: 'createLinkDialog.qrCode' })}
            </LoadingButton>
            <LoadingButton onClick={() => navigate("/settings/links")}>
              {intl.formatMessage({ id: 'createLinkDialog.viewLinks' })}
            </LoadingButton>
          </DialogActions>
        </>
      )}
      <QrCodeDialog open={qrOpen} onClose={() => setQrOpen(false)} url={linkCreated ?? ""} />
    </Dialog>
  );
}
